import { useComputed } from '@preact/signals';
import { h } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import { PREROLL } from '../../../../../ad/types';
import { TOGGLE_SHOW_PANEL_LIVE_OPTION, UI_FULLSCREEN_ANIMATION_PLAYED, UI_SHINY_ANIMATION } from '../../../../../store/types';
import {
  USER_CLICK,
  USER_KEYBOARD,
  USER_REMOTE_CHAT_CLICKED,
  USER_REMOTE_HIGHLIGHTS_CLICKED,
  USER_REMOTE_INFOS_CLICKED,
  USER_REMOTE_PLAYLIST_CLICKED,
  USER_REMOTE_RECO_CLICKED,
  USER_REMOTE_ZAPPING_CLICKED
} from '../../../../../types';
import {
  CHAT_NAME,
  EMPTY_ACTIVE_NAME,
  HIGHLIGHTS_NAME,
  INFOS_NAME,
  PLAYLIST_NAME,
  RECO_NAME,
  ZAPPING_NAME
} from '../../../../common/components/panel/constants';
import { BUTTON_LAYER, FINGER_UI, MAX_LAYER } from '../../../../common/constants';
import { useNewStore, usePanelAnimation, usePlayerContext, useStyles } from '../../../../common/hooks';
import { isValidateKeyboard } from '../../../../common/utils/keyboard';
import { togglePanel } from '../../../../common/utils/panel';
import { withBreakPoints } from '../../../hoc';
import { Button } from '../../common';
import './Animations/CompactShiny/CompactShiny.css';
import CompactShinyAtoms from './Animations/CompactShiny/CompactShinyAtoms';
import ZapetteFullscreenChannels, { animationLogoUrls } from './Animations/ZapetteFullscreen/ZapetteFullscreen';
import './Animations/ZapetteFullscreen/ZapetteFullscreen.css';
import { ZAP_BUTTON_SIZE_OVERRIDE, ZAP_CONTENT, ZAP_GLASS_CONTENT, ZAP_ICON_SIZE, ZAP_WRAPPER } from './styles';

const handleClick = ({ player, setActive, isActive, name, action, source }) => {
  const activePanel = isActive ? EMPTY_ACTIVE_NAME : name;
  setActive(activePanel);
  togglePanel(player, { show: !isActive, source, name: activePanel });

  player.userEvents$.next({
    action,
    value: !isActive,
    source
  });
};

function Zap() {
  const player = usePlayerContext();

  const uiStore = useNewStore('ui');
  const highlightsStore = useNewStore('highlights', player);
  const mediaStore = useNewStore('media', player);
  const adStore = useNewStore('ad', player);
  const recommendationsStore = useNewStore('recommendations', player);
  const playlistStore = useNewStore('playlist', player);
  const zappingStore = useNewStore('zapping');

  const setStyles = useStyles();
  const [currentTab, setActive] = useState(uiStore.$panelLiveOption.value.currentTab);
  const { size } = setStyles(ZAP_ICON_SIZE);
  const zapContentStyle = setStyles(ZAP_CONTENT);

  const $zappingEnabled = useComputed(() => mediaStore.$isLive.value && zappingStore.$zappingEnabled.value);

  const $hasChat = useComputed(() => (
    mediaStore.$isLive.value
    && uiStore.$chatAvailable.value
    && uiStore.$isFullscreen.value
    && !uiStore.$isSmallScreen.value
    && !uiStore.$isExtraSmallScreen.value
  ));
  const $hasPlaylist = useComputed(() => (
    !mediaStore.$isLive.value
    && playlistStore.$playlist.value.length > 0
  ));
  const $hasHighlight = useComputed(() => (
    mediaStore.$isLive.value
    && !!highlightsStore.$pinsPosition.value.length
  ));
  const $hasReco = useComputed(() => (
    !mediaStore.$isLive.value
    && recommendationsStore.$recommendations.value.length > 0
    && uiStore.$isFullscreen.value
  ));
  const $hasInfos = useComputed(() => (
    !mediaStore.$isLive.value
    && !!mediaStore.$description.value
    && uiStore.$isFullscreen.value
  ));

  const $show = useComputed(() => (
    (!adStore.$adType.value || (adStore.$adType.value && adStore.$adType.value !== PREROLL && mediaStore.$isDAI.value))
    && !uiStore.$isExtraSmallScreen.value && !uiStore.$waitStart.value
    && (
      (mediaStore.$isLive.value && ($zappingEnabled.value || $hasHighlight.value || $hasChat.value))
      || (!mediaStore.$isLive.value && ($hasPlaylist.value || $hasReco.value || $hasInfos.value))
    )
  ));

  const $animationName = usePanelAnimation({
    $show,
    $isFullscreen: uiStore.$isFullscreen,
    $fullscreenAnimationPlayed: uiStore.$fullscreenAnimationPlayed,
    $zappingEnabled,
    $currentBreakpoint: uiStore.$currentBreakpoint,
    $stopShinyAnimation: uiStore.$stopShinyAnimation
  });

  /* Required for close button to reset state */
  const isZappingActive = currentTab === ZAPPING_NAME;
  const isHighlightsActive = currentTab === HIGHLIGHTS_NAME;
  const isChatActive = currentTab === CHAT_NAME;
  const isPlaylistActive = currentTab === PLAYLIST_NAME;
  const isRecoActive = currentTab === RECO_NAME;
  const isInfoActive = currentTab === INFOS_NAME;

  const btns = [
    {
      name: PLAYLIST_NAME,
      showBtn: $hasPlaylist.value,
      label: 'Du même programme',
      ariaLabel: 'Accéder à la liste des vidéos du même programme',
      icon: isPlaylistActive ? 'playlist-active' : 'playlist',
      isActive: isPlaylistActive,
      type: TOGGLE_SHOW_PANEL_LIVE_OPTION,
      action: USER_REMOTE_PLAYLIST_CLICKED
    },
    {
      name: RECO_NAME,
      showBtn: $hasReco.value,
      label: 'Recommandé pour vous',
      ariaLabel: 'Accéder à la liste des vidéos recommandées pour vous',
      icon: isRecoActive ? 'heart-active' : 'heart',
      isActive: isRecoActive,
      type: TOGGLE_SHOW_PANEL_LIVE_OPTION,
      action: USER_REMOTE_RECO_CLICKED
    },
    {
      name: INFOS_NAME,
      showBtn: $hasInfos.value,
      label: 'Infos',
      ariaLabel: 'Accéder au résumé de la vidéo',
      icon: isInfoActive ? 'infos-active' : 'infos',
      isActive: isInfoActive,
      type: TOGGLE_SHOW_PANEL_LIVE_OPTION,
      action: USER_REMOTE_INFOS_CLICKED
    },
    {
      name: HIGHLIGHTS_NAME,
      showBtn: $hasHighlight.value,
      label: 'Les temps forts',
      ariaLabel: 'Accéder à la liste des moments forts',
      isActive: isHighlightsActive,
      type: TOGGLE_SHOW_PANEL_LIVE_OPTION,
      action: USER_REMOTE_HIGHLIGHTS_CLICKED,
      icon: !isHighlightsActive ? 'star' : 'starActive'
    },
    {
      name: CHAT_NAME,
      showBtn: $hasChat.value,
      label: 'Live chat',
      ariaLabel: 'Accéder au live chat',
      isActive: isChatActive,
      type: TOGGLE_SHOW_PANEL_LIVE_OPTION,
      action: USER_REMOTE_CHAT_CLICKED,
      icon: !isChatActive ? 'chat' : 'chatActive'
    },
    {
      name: ZAPPING_NAME,
      showBtn: $zappingEnabled.value,
      label: 'Tous les directs',
      ariaLabel: 'Accéder à la liste des directs',
      isActive: isZappingActive,
      type: TOGGLE_SHOW_PANEL_LIVE_OPTION,
      action: USER_REMOTE_ZAPPING_CLICKED,
      icon: isZappingActive ? 'remoteControlActive' : 'remoteControl'
    }
  ];

  const { gap, padding } = zapContentStyle;
  const paddingTotal = useMemo(() => {
    const paddingIsNumber = typeof padding === 'number';
    let pad = 0;
    if (paddingIsNumber) {
      pad = padding;
    } else {
      const paddings = padding?.replace(/px/g, '')?.split(' ');
      if (paddings?.length) {
        pad = Number(paddings[0]);
        if (paddings.length > 2) {
          pad += Number(paddings[2]);
        } else {
          pad *= 2;
        }
      }
    }
    return pad;
  }, [padding]);
  const nbButtons = btns.filter(({ showBtn }) => showBtn).length;
  const height = paddingTotal + (size * nbButtons) + (gap * (nbButtons - 1));
  const maxHeight = height + animationLogoUrls.length * size + 1 + (gap * (animationLogoUrls.length + 1));

  const animationComponent = useComputed(() => {
    switch ($animationName.value) {
      case 'fullscreen': {
        return <ZapetteFullscreenChannels size={`${size}px`} />;
      }
      case 'shiny':
        return <CompactShinyAtoms />;
      default:
    }
    return '';
  });

  return $show.value && (
    <div
      id="panel-button-wrapper"
      className={`ftv-magneto--animation-deploy-${$animationName.value}`}
      onAnimationEnd={(e) => {
        if ($animationName.value === 'fullscreen' && e.animationName === 'deployOut') {
          player.store.dispatch({ type: UI_FULLSCREEN_ANIMATION_PLAYED, payload: true });
        }
        if (uiStore.$display.value === FINGER_UI && $animationName.value === 'shiny' && e.animationName === 'pulse') {
          player.store.dispatch({ type: UI_SHINY_ANIMATION, payload: true });
        }
      }}
      style={{
        ...ZAP_WRAPPER,
        zIndex: ['xs', 'sm'].includes(uiStore.$currentBreakpoint.value) ? 'auto' : MAX_LAYER,
        '--amorce-height': `${height}px`,
        '--amorce-max-height': `${maxHeight}px`
      }}
      onClick={(e) => e.stopPropagation()}
      role="presentation"
    >
      {/* 👇 This is the original container with the blur effect */}
      <div className="panel-wrapper ftv-magneto--glass ftv-magneto--glass--bordered ftv-magneto--glass-blur-medium" style={ZAP_GLASS_CONTENT}>
        {/* 👇 This container exist only for the "Compact shiny" animation on the buttons */}
        <div className={`amorce-${$animationName.value}`} style={setStyles(ZAP_CONTENT)}>
          {
            btns.map(({ name, showBtn, label, ariaLabel, isActive, type, action, icon }) => (showBtn && (
              <Button
                styleOverride={ZAP_BUTTON_SIZE_OVERRIDE}
                styles={{
                  subWrapperStyle: {
                    height: 'inherit'
                  }
                }}
                label={label}
                ariaLabel={ariaLabel}
                ariaHidden="false"
                onClick={(e) => e instanceof PointerEvent && handleClick({
                  player,
                  setActive,
                  isActive,
                  type,
                  name,
                  action,
                  source: USER_CLICK
                })}
                xs
                onKeyDown={(e) => isValidateKeyboard(e) && handleClick({
                  player,
                  setActive,
                  isActive,
                  type,
                  name,
                  action,
                  source: USER_KEYBOARD
                })}
                name={name}
                disableFocus={false}
                zIndex={BUTTON_LAYER + 1}
                type="icon"
                icon={icon}
                size={size}
                zone="left"
              />
            )))
          }
          {animationComponent}
        </div>
      </div>
    </div>
  );
}

export default withBreakPoints(Zap);
